import type { TFunction } from 'i18next';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import type { ColumnRenderProps } from 'react-table';

import type { IColumn } from '@feathr/components';
import { ContextMenu, Pill, TableColumnHeader, Time } from '@feathr/components';
import { getIconForAction, TimeFormat } from '@feathr/hooks';

import DemoMenuItem from '../components/DemoMenuItem';
import type { ISampleConversion } from '../sampleData';
import buildOptionsColumn from './buildOptionsColumn';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

interface IConversionsColumnsArgs {
  base: string;
  t: TFunction;
}

function conversionsColumns({
  base,
  t,
}: IConversionsColumnsArgs): Array<IColumn<ISampleConversion>> {
  return [
    {
      accessor: 'name',
      id: 'name',
      Header: TableColumnHeader({ title: t('Name'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      minWidth: 280,
      Cell({ original }): JSX.Element {
        return <Link to={base}>{original.name}</Link>;
      },
      Footer(): JSX.Element {
        return <strong>{t('Page totals')}</strong>;
      },
    },
    {
      accessor: 'type',
      id: 'type',
      Header: TableColumnHeader({ title: t('Type'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      width: 120,
      Cell({ original }): JSX.Element {
        return <Pill theme={'neutral'}>{original.type}</Pill>;
      },
    },
    {
      accessor: 'count',
      id: 'count',
      Header: TableColumnHeader({ title: t('Count'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 120,
      Cell({ original }): JSX.Element {
        return <>{numeral(original.count).format('0,0')}</>;
      },
      Footer({ data }: ColumnRenderProps<{ _original: ISampleConversion }>): JSX.Element {
        const total = data.reduce((subtotal, row) => subtotal + row._original.count, 0);
        return <>{numeral(total).format('0,0')}</>;
      },
    },
    {
      accessor: (row): number => row.value ?? -1,
      id: 'value',
      Header: TableColumnHeader({ title: t('Value'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 140,
      Cell({ original }): JSX.Element {
        if (!original.value) {
          return <>{t('—')}</>;
        }
        return <>{numeral(original.value).format('$0,0.00')}</>;
      },
      Footer({ data }: ColumnRenderProps<{ _original: ISampleConversion }>): JSX.Element {
        const total = data.reduce((subtotal, row) => subtotal + (row._original.value ?? 0), 0);
        return <>{numeral(total).format('$0,0.00')}</>;
      },
    },
    {
      accessor: 'lastTracked',
      id: 'lastTracked',
      Header: TableColumnHeader({ title: t('Last tracked'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellCenter,
      sortable: true,
      width: 160,
      Cell({ original }): JSX.Element {
        return <Time format={TimeFormat.timeFromNow} timestamp={original.lastTracked} />;
      },
    },
    buildOptionsColumn<ISampleConversion>({
      t,
      renderItems: (): JSX.Element => (
        <>
          <ContextMenu.Item link={base} prefix={getIconForAction('edit')}>
            {t('Edit')}
          </ContextMenu.Item>
          <DemoMenuItem action={'archive'} isDestructive={true} label={t('Archive')} />
        </>
      ),
    }),
  ];
}

export default conversionsColumns;
